import React from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line
} from 'recharts'
import { DtcTrendDataPoint, DtcPlatform, useDtcKpis } from '../lib/dtc-data-service'
import { cn } from '../lib/utils'

interface DtcRevenueTrendChartProps {
  data: DtcTrendDataPoint[] | undefined
  platforms: DtcPlatform[] | undefined
  days?: number
  className?: string
}

// Platform colors matching the Summary page
const PLATFORM_COLORS: Record<string, string> = {
  shopify: '#95BF47',
  amazon_seller_central: '#FF9900',
  walmart: '#0071CE'
}

// Abbreviated format for Y-axis labels (e.g., $50K, $1.2M)
const formatCurrencyShort = (value: number) => {
  if (!Number.isFinite(value)) return '$0'
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

// Full format with commas for tooltips (e.g., $58,978)
const formatCurrency = (value: number) => {
  if (!Number.isFinite(value)) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return '0'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatDateLabel = (dateString: string) => {
  try {
    // Handle both date formats: "2025-11-07" and "2025-11-07T00:00:00.000Z"
    // CRITICAL: Always use 'Z' suffix and timeZone: 'UTC' to prevent date shift
    const dateStr = dateString.includes('T') ? dateString : dateString + 'T00:00:00Z'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  } catch {
    return dateString
  }
}

const tooltipFormatter = (value: number, name: string) => {
  if (name === 'total_revenue') {
    return [formatCurrency(value), 'Revenue']
  }
  if (name === 'total_orders') {
    return [formatNumber(value), 'Orders']
  }
  return [value, name]
}

const tooltipLabelFormatter = (label: any, payload: any[]) => {
  if (!payload || payload.length === 0) return ''
  const point = payload[0].payload as DtcTrendDataPoint
  if (point.date) {
    try {
      // Handle both date formats: "2025-11-07" and "2025-11-07T00:00:00.000Z"
      // CRITICAL: Always use 'Z' suffix and timeZone: 'UTC' to prevent date shift
      const dateStr = point.date.includes('T') ? point.date : point.date + 'T00:00:00Z'
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }) + ' (UTC)'
    } catch {
      return point.date
    }
  }
  return label
}

export function DtcRevenueTrendChart({ data, platforms, days, className }: DtcRevenueTrendChartProps) {
  // Get platform IDs for fixed hooks (to avoid Rules of Hooks violations)
  const platform1Id = platforms?.[0]?.id
  const platform2Id = platforms?.[1]?.id
  const platform3Id = platforms?.[2]?.id
  
  // Fetch trend data for each platform (fixed number of hooks)
  const platform1Data = useDtcKpis(platform1Id || null, undefined, { days, view_type: 'calendar' })
  const platform2Data = useDtcKpis(platform2Id || null, undefined, { days, view_type: 'calendar' })
  const platform3Data = useDtcKpis(platform3Id || null, undefined, { days, view_type: 'calendar' })

  // Combine trend data from all platforms by date
  const combinedData = React.useMemo(() => {
    if (!data || data.length === 0) return []

    // Create a map of date -> combined data point
    const dataByDate = new Map<string, any>()

    // Initialize with total aggregated data
    data.forEach(point => {
      dataByDate.set(point.date, {
        date: point.date,
        total_revenue: Number(point.total_revenue) || 0,
        total_orders: Number(point.total_orders) || 0
      })
    })

    // Add platform-specific revenue for stacked bars
    const platformQueries = [
      { platform: platforms?.[0], data: platform1Data.data },
      { platform: platforms?.[1], data: platform2Data.data },
      { platform: platforms?.[2], data: platform3Data.data }
    ]

    platformQueries.forEach(({ platform, data: platformData }) => {
      if (platform && platformData?.trend) {
        platformData.trend.forEach((point: DtcTrendDataPoint) => {
          const existing = dataByDate.get(point.date)
          if (existing) {
            existing[`${platform.platform_type}_revenue`] = Number(point.total_revenue) || 0
          }
        })
      }
    })

    return Array.from(dataByDate.values()).sort((a, b) => {
      // CRITICAL: Always use 'Z' suffix to ensure UTC parsing for consistent sorting
      const aDateStr = a.date.includes('T') ? a.date : a.date + 'T00:00:00Z'
      const bDateStr = b.date.includes('T') ? b.date : b.date + 'T00:00:00Z'
      return new Date(aDateStr).getTime() - new Date(bDateStr).getTime()
    })
  }, [data, platforms, platform1Data.data, platform2Data.data, platform3Data.data])

  const isLoading = platform1Data.isLoading || platform2Data.isLoading || platform3Data.isLoading

  return (
    <div className={cn('bg-white border border-gray-200 rounded-xl shadow-sm', className)}>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Revenue Trend (UTC)</h3>
          <p className="text-xs text-gray-500">Daily revenue by platform • All dates in UTC to match Shopify reports</p>
        </div>
        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            Loading platform data...
          </div>
        )}
      </div>

      <div className="px-2 py-4 h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={combinedData} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateLabel}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tickFormatter={formatCurrencyShort}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                // Format platform names nicely
                if (name.includes('_revenue')) {
                  const platformType = name.replace('_revenue', '')
                  const platformName = platforms?.find(p => p.platform_type === platformType)?.platform_name || platformType
                  return [formatCurrency(value), platformName]
                }
                // Handle Total Revenue line (name is "Total Revenue", not "total_revenue")
                if (name === 'Total Revenue' || name === 'total_revenue') {
                  return [formatCurrency(value), 'Total Revenue']
                }
                return tooltipFormatter(value, name)
              }}
              labelFormatter={tooltipLabelFormatter}
              contentStyle={{ borderRadius: 8, borderColor: '#e5e7eb' }}
            />
            <Legend
              verticalAlign="top"
              align="left"
              height={36}
              wrapperStyle={{ fontSize: 12 }}
              formatter={(value: string) => {
                // Format legend labels
                if (value.includes('_revenue')) {
                  const platformType = value.replace('_revenue', '')
                  return platforms?.find(p => p.platform_type === platformType)?.platform_name || platformType
                }
                return value
              }}
            />
            
            {/* Stacked bars for each platform */}
            {platforms?.map((platform, index) => (
              <Bar
                key={platform.id}
                dataKey={`${platform.platform_type}_revenue`}
                name={`${platform.platform_type}_revenue`}
                stackId="revenue"
                fill={PLATFORM_COLORS[platform.platform_type] || '#9333ea'}
                maxBarSize={40}
                radius={index === platforms.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
              />
            ))}

            {/* Total revenue line */}
            <Line 
              type="monotone" 
              dataKey="total_revenue" 
              name="Total Revenue" 
              stroke="#9333ea" 
              strokeWidth={3} 
              dot={false} 
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default DtcRevenueTrendChart

